import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from '@/context/StoreContext';
import { api } from '@/lib/api';
import AdminLogin from './AdminLogin';
import { toast } from 'sonner';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Pencil, Trash2, Plus, LogOut, Package, ShoppingBag, MessageSquare, TrendingUp, Upload, X, BellRing,
  Mail, RefreshCw, Users, Search, CheckCircle2, AlertTriangle, Clock, History, Boxes, Save, PlusCircle, MinusCircle, Trash, FileText, Receipt, Download,
} from 'lucide-react';

const field = 'w-full bg-transparent border border-nw-gold/25 rounded-[3px] px-3 h-10 text-nw-ivory placeholder:text-nw-warmgrey/50 focus:outline-none focus:border-nw-gold text-sm';
const label = 'block text-[10px] tracking-luxe uppercase text-nw-warmgrey mb-1.5';
const th = 'p-3 text-left text-[10px] tracking-luxe uppercase text-nw-warmgrey';
const tabCls = 'data-[state=active]:bg-nw-gold data-[state=active]:text-nw-black text-nw-warmgrey text-xs tracking-wide2 uppercase';
const selectCls = 'h-9 w-36 bg-transparent border-nw-gold/25 text-nw-ivory text-xs';
const menuCls = 'bg-nw-espresso border-nw-gold/25 text-nw-ivory';

const EMPTY = {
  name: '', estate: '', region: '', country: '', category: 'wine', subcategory: '', type: '',
  vintage: '', format: '75cl', abv: 12.5, price_eur: 0, rarity: 'classic', availability: 'in_stock',
  stock: 0, images: [], description_en: '', description_fr: '', on_request: false, featured: false, section: '',
};

const imgSrc = (u) => (u?.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

// Safe numeric coercion — blank / invalid input falls back to `d` instead of sending NaN → null (422).
const num = (v, d = 0) => { const n = Number(v); return Number.isFinite(n) ? n : d; };

// Turn any axios / FastAPI error into a short readable string (422 returns detail as an array of objects).
const readErr = (e, fallback = 'Something went wrong — please try again') => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    const msgs = d.map((x) => {
      const field = Array.isArray(x?.loc) ? x.loc[x.loc.length - 1] : '';
      return `${field ? `${field}: ` : ''}${x?.msg || ''}`.trim();
    }).filter(Boolean);
    if (msgs.length) return msgs.join(' · ');
  }
  if (d && typeof d === 'object' && d.msg) return d.msg;
  return e?.message || fallback;
};

/* ---------------------------------- photos ---------------------------------- */
function PhotoManager({ product, onChanged }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const images = product?.images || [];

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      let updated = product;
      for (const f of Array.from(files)) {
        updated = await api.adminUploadProductImage(product.id, f, 'first');
      }
      toast.success(`${files.length} photo${files.length > 1 ? 's' : ''} uploaded`);
      onChanged(updated);
    } catch (e) { toast.error(readErr(e, 'Upload failed')); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ''; }
  };
  const remove = async (url) => {
    try { const updated = await api.adminRemoveProductImage(product.id, url); onChanged(updated); toast.success('Photo removed'); }
    catch (e) { toast.error(readErr(e, 'Could not remove photo')); }
  };

  return (
    <div data-testid="admin-photo-manager" className="border border-nw-gold/20 p-4 bg-nw-black/40">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className={label + ' mb-0'}>Your own photographs</div>
          <div className="text-[11px] text-nw-warmgrey/80 mt-1">JPEG / PNG / WebP · auto-fitted to the 3:4 studio format · the first photo is shown on the card.</div>
        </div>
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={(e) => upload(e.target.files)} data-testid="admin-photo-input" />
        <button type="button" data-testid="admin-photo-upload-button" disabled={busy} onClick={() => inputRef.current?.click()} className="nw-btn-gold h-9 px-4 text-[11px]">
          <Upload size={14} /> {busy ? 'Uploading…' : 'Upload photo'}
        </button>
      </div>
      <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
        {images.map((u, i) => (
          <div key={u} className="relative group aspect-[3/4] bg-nw-espresso border border-nw-gold/15 overflow-hidden">
            <img src={imgSrc(u)} alt="" className="w-full h-full object-cover" loading="lazy" />
            {i === 0 && <span className="absolute bottom-1 left-1 text-[9px] tracking-luxe uppercase bg-nw-gold text-nw-black px-1.5 py-0.5">Main</span>}
            {u.startsWith('/api/uploads/') && <span className="absolute top-1 left-1 text-[9px] tracking-luxe uppercase bg-nw-black/70 text-nw-gold px-1.5 py-0.5">Own</span>}
            <button type="button" aria-label="Remove photo" data-testid={`admin-photo-remove-${i}`} onClick={() => remove(u)}
              className="absolute top-1 right-1 h-6 w-6 grid place-items-center bg-nw-black/80 text-nw-ivory opacity-0 group-hover:opacity-100 transition-opacity hover:text-nw-gold">
              <X size={12} />
            </button>
          </div>
        ))}
        {images.length === 0 && <div className="col-span-full text-xs text-nw-warmgrey py-4 text-center">No photo yet.</div>}
      </div>
    </div>
  );
}

/* ---------------------------------- product form ---------------------------------- */
function ProductForm({ initial, onSaved, onProductChanged }) {
  const [f, setF] = useState({ ...EMPTY, ...initial, images: (initial?.images || []).join(', ') });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const name = String(f.name || '').trim();
    if (!name) { toast.error('Please enter a product name before saving'); return; }
    setSaving(true);
    const payload = {
      ...f,
      name,
      abv: num(f.abv), price_eur: num(f.price_eur), stock: Math.max(0, Math.round(num(f.stock))),
      vintage: f.vintage ? String(f.vintage) : null,
      on_request: !!f.on_request, featured: !!f.featured,
      images: String(f.images).split(',').map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (initial?.id) await api.adminUpdateProduct(initial.id, payload);
      else await api.adminCreateProduct(payload);
      toast.success('Saved'); onSaved();
    } catch (e) { toast.error(readErr(e, 'Save failed')); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
      {initial?.id && (
        <PhotoManager product={initial} onChanged={(p) => { setF((s) => ({ ...s, images: (p.images || []).join(', ') })); onProductChanged?.(p); }} />
      )}
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><label className={label}>Name</label><input data-testid="admin-product-name" className={field} value={f.name} onChange={set('name')} /></div>
        <div><label className={label}>Estate</label><input className={field} value={f.estate} onChange={set('estate')} /></div>
        <div><label className={label}>Region</label><input className={field} value={f.region} onChange={set('region')} /></div>
        <div><label className={label}>Category</label>
          <Select value={f.category} onValueChange={set('category')}><SelectTrigger className={field}><SelectValue /></SelectTrigger>
            <SelectContent className={menuCls}><SelectItem value="wine">wine</SelectItem><SelectItem value="spirit">spirit</SelectItem></SelectContent></Select></div>
        <div><label className={label}>Subcategory</label><input className={field} value={f.subcategory} onChange={set('subcategory')} placeholder="Bordeaux, Cognac…" /></div>
        <div><label className={label}>Type</label><input className={field} value={f.type} onChange={set('type')} placeholder="red, whisky…" /></div>
        <div><label className={label}>Vintage</label><input className={field} value={f.vintage || ''} onChange={set('vintage')} /></div>
        <div><label className={label}>Format</label><input className={field} value={f.format} onChange={set('format')} /></div>
        <div><label className={label}>ABV %</label><input type="number" step="0.1" className={field} value={f.abv} onChange={set('abv')} /></div>
        <div><label className={label}>Price (EUR)</label><input data-testid="admin-product-price" type="number" className={field} value={f.price_eur} onChange={set('price_eur')} /></div>
        <div><label className={label}>Stock</label><input data-testid="admin-product-stock" type="number" className={field} value={f.stock} onChange={set('stock')} /></div>
        <div><label className={label}>Rarity</label>
          <Select value={f.rarity} onValueChange={set('rarity')}><SelectTrigger className={field}><SelectValue /></SelectTrigger>
            <SelectContent className={menuCls}>{['classic', 'rare', 'exceptional', 'collector'].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select></div>
        <div><label className={label}>Availability</label>
          <Select value={f.availability} onValueChange={set('availability')}><SelectTrigger data-testid="admin-product-availability" className={field}><SelectValue /></SelectTrigger>
            <SelectContent className={menuCls}>{['in_stock', 'low_stock', 'out_of_stock', 'on_request'].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select></div>
        <div><label className={label}>Section</label>
          <Select value={f.section || 'none'} onValueChange={(v) => set('section')(v === 'none' ? '' : v)}><SelectTrigger className={field}><SelectValue /></SelectTrigger>
            <SelectContent className={menuCls}>{['none', 'grand_wines', 'exceptional_spirits', 'rare_treasures'].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select></div>
      </div>
      <div><label className={label}>Image URLs (comma separated — advanced)</label><input className={field} value={f.images} onChange={set('images')} /></div>
      <div><label className={label}>Description (EN)</label><textarea rows={2} className={field + ' h-auto py-2'} value={f.description_en} onChange={set('description_en')} /></div>
      <div><label className={label}>Description (FR)</label><textarea rows={2} className={field + ' h-auto py-2'} value={f.description_fr} onChange={set('description_fr')} /></div>
      <div className="flex items-center gap-6 pt-1">
        <label className="flex items-center gap-2 text-sm text-nw-warmgrey"><input type="checkbox" checked={f.on_request} onChange={(e) => set('on_request')(e.target.checked)} /> On request</label>
        <label className="flex items-center gap-2 text-sm text-nw-warmgrey"><input type="checkbox" checked={f.featured} onChange={(e) => set('featured')(e.target.checked)} /> Featured</label>
      </div>
      <p className="text-[11px] text-nw-warmgrey/80">Restock alerts: when a product goes from unavailable to purchasable (stock &gt; 0 and in stock / low stock), every subscriber automatically receives a "back in cellar" e-mail on save.</p>
      <button data-testid="admin-product-save" onClick={save} disabled={saving} className="nw-btn-gold w-full mt-2">{saving ? '…' : 'Save product'}</button>
    </div>
  );
}

/* ---------------------------------- change-log helpers ---------------------------------- */
const chVal = (v) => {
  if (v === true) return 'yes';
  if (v === false) return 'no';
  if (v === null || v === undefined || v === '') return '∅';
  const s = String(v);
  return s.length > 28 ? `${s.slice(0, 28)}…` : s;
};

function ActionBadge({ action }) {
  const map = {
    created: ['text-nw-gold border-nw-gold/40', PlusCircle, 'created'],
    updated: ['text-nw-ivory border-nw-gold/25', Pencil, 'updated'],
    deleted: ['text-[#d9a066] border-[#d9a066]/40', Trash, 'deleted'],
  };
  const [cls, Icon, text] = map[action] || map.updated;
  return <span className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] tracking-luxe uppercase ${cls}`}><Icon size={11} /> {text}</span>;
}

/* ---------------------------------- status badge ---------------------------------- */
function EmailStatus({ status }) {
  const map = {
    sent: ['text-nw-gold border-nw-gold/40', CheckCircle2, 'sent'],
    simulated: ['text-nw-warmgrey border-nw-gold/20', Clock, 'simulated'],
    failed: ['text-[#d9a066] border-[#d9a066]/40', AlertTriangle, 'failed'],
    superseded: ['text-nw-warmgrey/60 border-nw-gold/10', RefreshCw, 'retried'],
    queued: ['text-nw-warmgrey border-nw-gold/20', Clock, 'queued'],
  };
  const [cls, Icon, text] = map[status] || map.queued;
  return <span className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] tracking-luxe uppercase ${cls}`}><Icon size={11} /> {text}</span>;
}

/* ---------------------------------- dashboard ---------------------------------- */
export default function AdminDashboard() {
  const { token, admin, logout, formatPrice } = useStore();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [emails, setEmails] = useState({ items: [], provider: '', sender: '' });
  const [restock, setRestock] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [enqFilter, setEnqFilter] = useState('all');
  const [enqSearch, setEnqSearch] = useState('');
  const [testTo, setTestTo] = useState('');
  const [emailBusy, setEmailBusy] = useState(false);
  const [notifying, setNotifying] = useState(null);
  const [changes, setChanges] = useState([]);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkStock, setBulkStock] = useState({});
  const [bulkSaving, setBulkSaving] = useState(false);

  const load = () => {
    api.adminStats().then(setStats).catch(() => {});
    api.products({ limit: 200, sort: 'name_asc' }).then((d) => setProducts(d.items)).catch(() => {});
    api.adminOrders().then((d) => setOrders(d.items)).catch(() => {});
    api.adminEnquiries().then((d) => setEnquiries(d.items || d)).catch(() => {});
    api.adminCustomers().then((d) => setCustomers(d.items)).catch(() => {});
    api.adminEmails().then(setEmails).catch(() => {});
    api.adminRestockSummary().then(setRestock).catch(() => {});
    api.adminProductChanges({ limit: 120 }).then((d) => setChanges(d.items)).catch(() => {});
  };
  useEffect(() => { if (token) load(); /* eslint-disable-next-line */ }, [token]);

  const filteredEnquiries = useMemo(() => enquiries.filter((e) => {
    if (enqFilter !== 'all' && e.kind !== enqFilter) return false;
    if (!enqSearch) return true;
    const hay = `${e.name || ''} ${e.email || ''} ${e.company || ''} ${e.product_name || ''} ${e.message || ''} ${e.category || ''}`.toLowerCase();
    return hay.includes(enqSearch.toLowerCase());
  }), [enquiries, enqFilter, enqSearch]);

  if (!token) return <AdminLogin />;

  const del = async (id) => { try { await api.adminDeleteProduct(id); toast.success('Deleted'); load(); } catch (e) { toast.error(readErr(e, 'Delete failed')); } };
  const updOrder = async (id, s) => { try { await api.adminUpdateOrder(id, s); toast.success('Updated'); load(); } catch (e) { toast.error(readErr(e)); } };
  const dlDoc = async (oid, kind) => { try { await api.adminDownloadOrderPdf(oid, kind); } catch (e) { toast.error(readErr(e, 'Document not available yet')); } };
  const updEnquiry = async (kind, id, s) => { try { await api.adminUpdateEnquiry(kind, id, s); toast.success('Updated'); load(); } catch (e) { toast.error(readErr(e)); } };
  const notifyRestock = async (p) => {
    setNotifying(p.id);
    try { const r = await api.adminNotifyRestock(p.id); toast.success(`${r.notified} subscriber${r.notified === 1 ? '' : 's'} notified (${r.provider})`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    finally { setNotifying(null); }
  };
  const sendTest = async () => {
    if (!testTo) return;
    setEmailBusy(true);
    try {
      const r = await api.adminEmailTest(testTo, 'fr');
      if (r.status === 'sent') toast.success(`Delivered via ${r.provider} to ${testTo}`);
      else if (r.status === 'simulated') toast.info('Provider not configured — e-mail stored in the outbox only');
      else toast.error(`Send failed: ${r.error || 'unknown error'}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    finally { setEmailBusy(false); }
  };
  const retryFailed = async () => {
    setEmailBusy(true);
    try { const r = await api.adminEmailRetryFailed(); toast.success(`${r.sent}/${r.retried} e-mails re-sent`); load(); }
    catch { toast.error('Error'); }
    finally { setEmailBusy(false); }
  };

  // ---- bulk stock edit
  const startBulk = () => {
    const m = {}; products.forEach((p) => { m[p.id] = p.stock; }); setBulkStock(m); setBulkMode(true);
  };
  const cancelBulk = () => { setBulkMode(false); setBulkStock({}); };
  const setBulkVal = (id, v) => setBulkStock((s) => ({ ...s, [id]: v }));
  const stepBulk = (id, delta) => setBulkStock((s) => ({ ...s, [id]: Math.max(0, num(s[id]) + delta) }));
  const bulkUpdates = products
    .filter((p) => bulkMode && bulkStock[p.id] !== '' && bulkStock[p.id] != null && Math.round(num(bulkStock[p.id])) !== p.stock)
    .map((p) => ({ id: p.id, stock: Math.max(0, Math.round(num(bulkStock[p.id]))) }));
  const saveBulk = async () => {
    if (!bulkUpdates.length) { toast.info('No stock changes to save'); return; }
    setBulkSaving(true);
    try {
      const r = await api.adminBulkStock(bulkUpdates);
      toast.success(`${r.updated} product${r.updated === 1 ? '' : 's'} updated${r.restocked ? ` · ${r.restocked} back in cellar` : ''}`);
      cancelBulk(); load();
    } catch (e) { toast.error(readErr(e, 'Bulk update failed')); }
    finally { setBulkSaving(false); }
  };

  const failedCount = emails.items.filter((e) => e.status === 'failed').length;
  const newEnq = enquiries.filter((e) => e.status === 'new' || e.status === 'pending').length;
  const statCards = stats ? [
    { icon: Package, label: 'Products', value: stats.products },
    { icon: ShoppingBag, label: 'Paid Orders', value: `${stats.paid_orders}/${stats.orders}` },
    { icon: TrendingUp, label: 'Revenue', value: formatPrice(stats.revenue_eur) },
    { icon: MessageSquare, label: 'Enquiries', value: `${newEnq} new / ${enquiries.length}` },
    { icon: Users, label: 'Customers', value: stats.customers ?? customers.length },
    { icon: Mail, label: 'E-mail provider', value: emails.provider || stats.email_provider || '—' },
  ] : [];

  return (
    <div className="bg-nw-black min-h-screen">
      <div className="nw-container py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="font-serif text-nw-gold text-2xl tracking-[0.2em]">NICEWINE</div>
            <div className="nw-eyebrow mt-1">Back-Office · {admin?.name}</div>
          </div>
          <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-2 text-[11px] tracking-luxe uppercase nw-link"><LogOut size={15} /> Logout</button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-10">
          {statCards.map((s, i) => (
            <div key={i} className="bg-nw-espresso border border-nw-gold/20 p-5">
              <s.icon className="text-nw-gold" size={20} strokeWidth={1.2} />
              <div className="text-xl font-serif text-nw-ivory mt-3 truncate">{s.value}</div>
              <div className="text-[10px] tracking-luxe uppercase text-nw-warmgrey mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="products">
          <TabsList className="bg-nw-espresso border border-nw-gold/20 flex-wrap h-auto">
            <TabsTrigger value="products" data-testid="admin-tab-products" className={tabCls}>Products</TabsTrigger>
            <TabsTrigger value="orders" data-testid="admin-tab-orders" className={tabCls}>Orders</TabsTrigger>
            <TabsTrigger value="enquiries" data-testid="admin-tab-enquiries" className={tabCls}>Enquiries {newEnq > 0 && <span className="ml-1.5 text-[9px] bg-nw-black/40 px-1.5 rounded-sm">{newEnq}</span>}</TabsTrigger>
            <TabsTrigger value="customers" data-testid="admin-tab-customers" className={tabCls}>Customers</TabsTrigger>
            <TabsTrigger value="emails" data-testid="admin-tab-emails" className={tabCls}>E-mails {failedCount > 0 && <span className="ml-1.5 text-[9px] bg-nw-black/40 px-1.5 rounded-sm">{failedCount} failed</span>}</TabsTrigger>
            <TabsTrigger value="history" data-testid="admin-tab-history" className={tabCls}>History</TabsTrigger>
          </TabsList>

          {/* PRODUCTS */}
          <TabsContent value="products" className="mt-6">
            <div className="flex items-center justify-end gap-3 mb-4">
              {!bulkMode ? (
                <button data-testid="admin-bulk-stock-start" onClick={startBulk}
                  className="inline-flex items-center gap-2 h-10 px-4 text-[11px] tracking-luxe uppercase border border-nw-gold/30 text-nw-warmgrey hover:text-nw-gold hover:border-nw-gold/60 transition-colors">
                  <Boxes size={15} /> Bulk stock edit
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <span data-testid="admin-bulk-count" className="text-[11px] tracking-luxe uppercase text-nw-warmgrey">
                    {bulkUpdates.length} change{bulkUpdates.length === 1 ? '' : 's'}
                  </span>
                  <button data-testid="admin-bulk-cancel" onClick={cancelBulk}
                    className="inline-flex items-center gap-2 h-10 px-4 text-[11px] tracking-luxe uppercase border border-nw-gold/25 text-nw-warmgrey hover:text-nw-ivory transition-colors">
                    Cancel
                  </button>
                  <button data-testid="admin-bulk-save" onClick={saveBulk} disabled={bulkSaving || bulkUpdates.length === 0} className="nw-btn-gold h-10">
                    <Save size={15} /> {bulkSaving ? 'Saving…' : 'Save stock'}
                  </button>
                </div>
              )}
              <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) setEditing(null); }}>
                <DialogTrigger asChild>
                  <button data-testid="admin-add-product-button" onClick={() => setEditing(null)} disabled={bulkMode} className="nw-btn-gold h-10 disabled:opacity-40"><Plus size={15} /> Add product</button>
                </DialogTrigger>
                <DialogContent className="bg-nw-espresso border-nw-gold/25 text-nw-ivory max-w-2xl">
                  <DialogHeader><DialogTitle className="font-serif text-xl text-nw-gold">{editing ? `Edit — ${editing.name}` : 'New product'}</DialogTitle></DialogHeader>
                  <ProductForm key={editing?.id || 'new'} initial={editing} onProductChanged={(p) => { setEditing(p); load(); }} onSaved={() => { setDialogOpen(false); setEditing(null); load(); }} />
                </DialogContent>
              </Dialog>
            </div>
            <div data-testid="admin-products-table" className="border border-nw-gold/15 overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-nw-gold/15">
                  <th className={th}></th><th className={th}>Name</th><th className={th}>Category</th><th className={th}>Price</th><th className={th}>Stock</th><th className={th}>Status</th><th className={th}>Alerts</th><th className={th}></th>
                </tr></thead>
                <tbody>
                  {products.map((p) => {
                    const pending = restock[p.id] || 0;
                    const purchasable = !p.on_request && p.stock > 0 && ['in_stock', 'low_stock'].includes(p.availability);
                    return (
                      <tr key={p.id} className="border-b border-nw-gold/8 hover:bg-nw-ivory/[0.03]">
                        <td className="p-2 w-12"><div className="w-9 aspect-[3/4] bg-nw-espresso overflow-hidden">{p.images?.[0] && <img src={imgSrc(p.images[0])} alt="" className="w-full h-full object-cover" loading="lazy" />}</div></td>
                        <td className="p-3 text-nw-ivory">{p.name}{p.images?.[0]?.startsWith('/api/uploads/') && <span className="ml-2 text-[9px] tracking-luxe uppercase text-nw-gold/80">own photo</span>}</td>
                        <td className="p-3 text-nw-warmgrey capitalize">{p.category} · {p.subcategory}</td>
                        <td className="p-3 text-nw-gold">{formatPrice(p.price_eur)}</td>
                        <td className="p-3 text-nw-warmgrey">
                          {bulkMode ? (
                            <div className="flex items-center gap-1">
                              <button type="button" aria-label="Decrease stock" data-testid={`admin-bulk-dec-${p.reference_id}`} onClick={() => stepBulk(p.id, -1)}
                                className="h-7 w-7 grid place-items-center border border-nw-gold/25 text-nw-warmgrey hover:text-nw-gold hover:border-nw-gold/60 transition-colors"><MinusCircle size={13} /></button>
                              <input type="number" min="0" data-testid={`admin-bulk-stock-${p.reference_id}`}
                                value={bulkStock[p.id] ?? ''} onChange={(e) => setBulkVal(p.id, e.target.value)}
                                className={`w-16 h-7 text-center bg-transparent border rounded-[3px] text-sm focus:outline-none ${Math.round(num(bulkStock[p.id])) !== p.stock ? 'border-nw-gold text-nw-gold' : 'border-nw-gold/25 text-nw-ivory'}`} />
                              <button type="button" aria-label="Increase stock" data-testid={`admin-bulk-inc-${p.reference_id}`} onClick={() => stepBulk(p.id, 1)}
                                className="h-7 w-7 grid place-items-center border border-nw-gold/25 text-nw-warmgrey hover:text-nw-gold hover:border-nw-gold/60 transition-colors"><PlusCircle size={13} /></button>
                            </div>
                          ) : p.stock}
                        </td>
                        <td className="p-3 text-nw-warmgrey text-xs">{p.availability}{p.on_request ? ' · on request' : ''}</td>
                        <td className="p-3 whitespace-nowrap">
                          {pending > 0 ? (
                            <button data-testid={`admin-notify-restock-${p.reference_id}`} onClick={() => notifyRestock(p)} disabled={!purchasable || notifying === p.id}
                              title={purchasable ? 'Send "back in cellar" e-mail now' : 'Set stock & availability first — subscribers are notified automatically on save'}
                              className={`inline-flex items-center gap-1.5 text-[10px] tracking-luxe uppercase border px-2 py-1 transition-colors ${purchasable ? 'border-nw-gold/50 text-nw-gold hover:bg-nw-gold hover:text-nw-black' : 'border-nw-gold/20 text-nw-warmgrey cursor-not-allowed'}`}>
                              <BellRing size={12} /> {pending} waiting
                            </button>
                          ) : <span className="text-nw-warmgrey/50 text-xs">—</span>}
                        </td>
                        <td className="p-3 text-right whitespace-nowrap">
                          <button data-testid={`admin-edit-${p.reference_id}`} onClick={() => { setEditing(p); setDialogOpen(true); }} className="text-nw-warmgrey hover:text-nw-gold p-1"><Pencil size={15} /></button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild><button data-testid={`admin-delete-${p.reference_id}`} className="text-nw-warmgrey hover:text-nw-gold p-1 ml-2"><Trash2 size={15} /></button></AlertDialogTrigger>
                            <AlertDialogContent className={menuCls}>
                              <AlertDialogHeader><AlertDialogTitle className="text-nw-ivory">Delete product?</AlertDialogTitle><AlertDialogDescription className="text-nw-warmgrey">{p.name} will be permanently removed.</AlertDialogDescription></AlertDialogHeader>
                              <AlertDialogFooter><AlertDialogCancel className="bg-transparent border-nw-gold/30 text-nw-ivory">Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(p.id)} className="bg-nw-gold text-nw-black">Delete</AlertDialogAction></AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* ORDERS */}
          <TabsContent value="orders" className="mt-6">
            <div data-testid="admin-orders-table" className="border border-nw-gold/15 overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-nw-gold/15">
                  <th className={th}>Order</th><th className={th}>Customer</th><th className={th}>Items</th><th className={th}>Total</th><th className={th}>Payment</th><th className={th}>Receipt</th><th className={th}>Invoice</th><th className={th}>Status</th>
                </tr></thead>
                <tbody>
                  {orders.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-nw-warmgrey">No orders yet.</td></tr>}
                  {orders.map((o) => (
                    <tr key={o.id} className="border-b border-nw-gold/8">
                      <td className="p-3 text-nw-gold whitespace-nowrap">{o.order_id}<div className="text-[11px] text-nw-warmgrey">{fmtDate(o.created_at)}</div></td>
                      <td className="p-3 text-nw-ivory">{o.customer?.first_name} {o.customer?.last_name}<div className="text-[11px] text-nw-warmgrey">{o.customer?.email}</div></td>
                      <td className="p-3 text-nw-warmgrey text-xs max-w-xs">{(o.items || []).map((it) => `${it.quantity}× ${it.name}`).join(' · ')}</td>
                      <td className="p-3 text-nw-ivory whitespace-nowrap">{formatPrice(o.total_eur)}</td>
                      <td className="p-3"><span className={`text-xs ${o.payment_status === 'paid' ? 'text-nw-gold' : 'text-nw-warmgrey'}`}>{o.payment_status}</span></td>
                      <td className="p-3">
                        {o.has_receipt ? (
                          <button data-testid={`order-receipt-${o.order_id}`} onClick={() => dlDoc(o.order_id, 'receipt')} title="Download receipt (PDF)"
                            className="inline-flex items-center gap-1.5 text-[11px] tracking-luxe uppercase text-nw-warmgrey hover:text-nw-gold transition-colors">
                            <Receipt size={14} /> PDF
                          </button>
                        ) : <span className="text-nw-warmgrey/40 text-xs">—</span>}
                      </td>
                      <td className="p-3">
                        {o.has_invoice ? (
                          <button data-testid={`order-invoice-${o.order_id}`} onClick={() => dlDoc(o.order_id, 'invoice')} title="Download invoice (PDF)"
                            className="inline-flex items-center gap-1.5 text-[11px] tracking-luxe uppercase text-nw-warmgrey hover:text-nw-gold transition-colors">
                            <FileText size={14} /> PDF
                          </button>
                        ) : <span className="text-nw-warmgrey/40 text-xs">—</span>}
                      </td>
                      <td className="p-3">
                        <Select value={o.status} onValueChange={(v) => updOrder(o.id, v)}>
                          <SelectTrigger className={selectCls}><SelectValue /></SelectTrigger>
                          <SelectContent className={menuCls}>{['pending', 'confirmed', 'preparing', 'shipped', 'delivered', 'cancelled'].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* ENQUIRIES */}
          <TabsContent value="enquiries" className="mt-6">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="flex gap-1 flex-wrap">
                {[['all', 'All'], ['quote', 'Quotes & Import/Export'], ['contact', 'Contact messages'], ['restock', 'Restock alerts']].map(([k, l]) => (
                  <button key={k} data-testid={`admin-enquiry-filter-${k}`} onClick={() => setEnqFilter(k)}
                    className={`text-[10px] tracking-luxe uppercase border px-3 py-1.5 transition-colors ${enqFilter === k ? 'bg-nw-gold text-nw-black border-nw-gold' : 'border-nw-gold/25 text-nw-warmgrey hover:text-nw-ivory'}`}>{l}</button>
                ))}
              </div>
              <div className="relative ml-auto">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-nw-warmgrey" />
                <input data-testid="admin-enquiry-search" className={field + ' pl-8 w-64 h-9'} placeholder="Search name, e-mail, product…" value={enqSearch} onChange={(e) => setEnqSearch(e.target.value)} />
              </div>
            </div>
            <div data-testid="admin-enquiries-table" className="border border-nw-gold/15 overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-nw-gold/15">
                  <th className={th}>Date</th><th className={th}>Category</th><th className={th}>Source</th><th className={th}>Client</th><th className={th}>Product / Message</th><th className={th}>Status</th>
                </tr></thead>
                <tbody>
                  {filteredEnquiries.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-nw-warmgrey">No enquiries.</td></tr>}
                  {filteredEnquiries.map((e) => (
                    <tr key={`${e.kind}-${e.id}`} className="border-b border-nw-gold/8 align-top">
                      <td className="p-3 text-nw-warmgrey text-xs whitespace-nowrap">{fmtDate(e.created_at)}</td>
                      <td className="p-3"><Badge variant="outline" className="border-nw-gold/40 text-nw-gold font-normal text-[10px] tracking-wide2 uppercase rounded-sm">{e.category}</Badge></td>
                      <td className="p-3 text-nw-warmgrey text-xs">{(e.source || '').replace(/_/g, ' ')}</td>
                      <td className="p-3 text-nw-ivory">{e.name || '—'}<div className="text-[11px] text-nw-warmgrey">{e.email}{e.company ? ` · ${e.company}` : ''}{e.delivery_country ? ` · ${e.delivery_country}` : ''}</div></td>
                      <td className="p-3 text-nw-warmgrey text-xs max-w-md">
                        {e.product_name && <div className="text-nw-ivory">{e.product_name}{e.quantity ? ` × ${e.quantity}` : ''}</div>}
                        {e.message && <div className="line-clamp-3">{e.message}</div>}
                      </td>
                      <td className="p-3">
                        {e.kind === 'restock' ? <span className="text-xs text-nw-warmgrey">{e.status}</span> : (
                          <Select value={e.status} onValueChange={(v) => updEnquiry(e.kind, e.id, v)}>
                            <SelectTrigger className={selectCls}><SelectValue /></SelectTrigger>
                            <SelectContent className={menuCls}>{['new', 'contacted', 'quoted', 'closed'].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* CUSTOMERS */}
          <TabsContent value="customers" className="mt-6">
            <div data-testid="admin-customers-table" className="border border-nw-gold/15 overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-nw-gold/15">
                  <th className={th}>Customer</th><th className={th}>Joined</th><th className={th}>Paid orders</th><th className={th}>Spent</th>
                </tr></thead>
                <tbody>
                  {customers.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-nw-warmgrey">No customer accounts yet.</td></tr>}
                  {customers.map((c) => (
                    <tr key={c.id} className="border-b border-nw-gold/8">
                      <td className="p-3 text-nw-ivory">{c.full_name}<div className="text-[11px] text-nw-warmgrey">{c.email}</div></td>
                      <td className="p-3 text-nw-warmgrey text-xs">{fmtDate(c.created_at)}</td>
                      <td className="p-3 text-nw-warmgrey">{c.paid_orders}</td>
                      <td className="p-3 text-nw-gold">{formatPrice(c.spent_eur || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* EMAILS */}
          <TabsContent value="emails" className="mt-6">
            <div className="grid lg:grid-cols-3 gap-4 mb-6">
              <div className="bg-nw-espresso border border-nw-gold/20 p-5">
                <div className={label}>Provider</div>
                <div data-testid="admin-email-provider" className="font-serif text-xl text-nw-ivory capitalize">{emails.provider || '—'}</div>
                <div className="text-[11px] text-nw-warmgrey mt-1">Sender: {emails.sender || '—'}</div>
                {emails.provider === 'simulated' && <div className="text-[11px] text-nw-warmgrey mt-2">E-mails are stored here but not delivered until SMTP / SendGrid credentials are configured.</div>}
              </div>
              <div className="bg-nw-espresso border border-nw-gold/20 p-5">
                <div className={label}>Send a test e-mail</div>
                <div className="flex gap-2">
                  <input data-testid="admin-email-test-to" className={field + ' h-9'} placeholder="you@example.com" value={testTo} onChange={(e) => setTestTo(e.target.value)} />
                  <button data-testid="admin-email-test-send" onClick={sendTest} disabled={emailBusy || !testTo} className="nw-btn-gold h-9 px-4 text-[11px] shrink-0"><Mail size={13} /> Send</button>
                </div>
              </div>
              <div className="bg-nw-espresso border border-nw-gold/20 p-5">
                <div className={label}>Failed deliveries</div>
                <div className="flex items-center justify-between gap-3">
                  <div className="font-serif text-xl text-nw-ivory">{failedCount}</div>
                  <button data-testid="admin-email-retry-failed" onClick={retryFailed} disabled={emailBusy || failedCount === 0} className="nw-btn-gold h-9 px-4 text-[11px]"><RefreshCw size={13} /> Retry all</button>
                </div>
              </div>
            </div>
            <div data-testid="admin-emails-table" className="border border-nw-gold/15 overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-nw-gold/15">
                  <th className={th}>Date</th><th className={th}>Kind</th><th className={th}>To</th><th className={th}>Subject</th><th className={th}>Status</th>
                </tr></thead>
                <tbody>
                  {emails.items.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-nw-warmgrey">No e-mails yet.</td></tr>}
                  {emails.items.map((e) => (
                    <tr key={e.id} className="border-b border-nw-gold/8 align-top">
                      <td className="p-3 text-nw-warmgrey text-xs whitespace-nowrap">{fmtDate(e.created_at)}</td>
                      <td className="p-3 text-nw-warmgrey text-xs">{(e.kind || '').replace(/_/g, ' ')}</td>
                      <td className="p-3 text-nw-ivory text-xs">{e.to}</td>
                      <td className="p-3 text-nw-warmgrey text-xs max-w-sm truncate">{e.subject}</td>
                      <td className="p-3"><EmailStatus status={e.status} />{e.error && <div className="text-[10px] text-[#d9a066]/90 mt-1 max-w-xs line-clamp-2" title={e.error}>{e.error}</div>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* HISTORY */}
          <TabsContent value="history" className="mt-6">
            <div className="flex items-center gap-2 mb-4">
              <History size={16} className="text-nw-gold" />
              <div className="nw-eyebrow">Product change history · who changed what, and when</div>
            </div>
            <div data-testid="admin-history-table" className="border border-nw-gold/15 overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-nw-gold/15">
                  <th className={th}>Date</th><th className={th}>By</th><th className={th}>Action</th><th className={th}>Product</th><th className={th}>Details</th>
                </tr></thead>
                <tbody>
                  {changes.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-nw-warmgrey">No changes recorded yet.</td></tr>}
                  {changes.map((c) => (
                    <tr key={c.id} data-testid="admin-history-row" className="border-b border-nw-gold/8 align-top">
                      <td className="p-3 text-nw-warmgrey text-xs whitespace-nowrap">{fmtDate(c.created_at)}</td>
                      <td className="p-3 text-nw-ivory text-xs whitespace-nowrap">{c.admin_name || '—'}</td>
                      <td className="p-3"><ActionBadge action={c.action} /></td>
                      <td className="p-3 text-nw-ivory">{c.product_name}{c.reference_id && <div className="text-[11px] text-nw-warmgrey">{c.reference_id}</div>}</td>
                      <td className="p-3 text-nw-warmgrey text-xs max-w-lg">
                        {(!c.changes || c.changes.length === 0) ? (
                          <span className="text-nw-warmgrey/60">{c.action === 'created' ? 'Product created' : c.action === 'deleted' ? 'Product removed' : '—'}</span>
                        ) : (
                          <div className="flex flex-wrap gap-1.5">
                            {c.changes.map((ch, i) => (
                              <span key={i} className="inline-flex items-center gap-1 border border-nw-gold/20 px-2 py-0.5 rounded-sm">
                                <span className="text-[10px] tracking-luxe uppercase text-nw-warmgrey">{ch.label}</span>
                                <span className="text-nw-warmgrey/60 line-through">{chVal(ch.old)}</span>
                                <span className="text-nw-gold">→ {chVal(ch.new)}</span>
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
